import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  useMediaQuery,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useTheme } from "@mui/material/styles";

import { useStyle } from "../../context/styleContext/styleContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
import Seo from "../../components/Seo";

function ProductListSchemaMarkup({ products }) {
  useEffect(() => {
    if (!products || products.length === 0) return;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Products",
      url: "https://parishaudio.com/products",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://parishaudio.com/products/${product.productURI}`,
          name: product.name,
        })),
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [products]);

  return null;
}

function inferProductGroup(product) {
  const values = [
    product?.category,
    product?.productCategory,
    product?.productType,
    product?.type,
    product?.group,
    product?.name,
    product?.shortDescription,
    product?.cardSummary,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    values.includes("plugin") ||
    values.includes("vst") ||
    values.includes("audio software") ||
    values.includes("software")
  ) {
    return "plugins";
  }

  if (
    values.includes("shirt") ||
    values.includes("t-shirt") ||
    values.includes("hoodie") ||
    values.includes("merch") ||
    values.includes("apparel")
  ) {
    return "merch";
  }

  return "other";
}

export default function ProductsPage() {
  const [height, setHeight] = useState(window.innerHeight);
  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [groupFilter, setGroupFilter] = useState("all");

  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const getProductInfo = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("/api/get-products-available-on-website");

      if (data) {
        setDataForAllProducts(data);
      } else {
        console.log("data error");
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductInfo();
  }, []);

  const filteredProducts = useMemo(() => {
    let products = [...dataForAllProducts];

    if (groupFilter !== "all") {
      products = products.filter(
        (product) => inferProductGroup(product) === groupFilter
      );
    }

    const query = searchTerm.trim().toLowerCase();
    if (query) {
      products = products.filter((product) => {
        const searchableText = [
          product.name,
          product.shortDescription,
          product.cardSummary,
          product.longDescription,
          product.category,
          product.productCategory,
          product.productType,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (sortBy === "name-asc") {
      products.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      products.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "price-low") {
      products.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === "price-high") {
      products.sort((a, b) => Number(b.price) - Number(a.price));
    } else if (sortBy === "rating") {
      products.sort((a, b) => {
        const aRating = Number(a.starRating || 0);
        const bRating = Number(b.starRating || 0);
        const aCount = Number(a.ratingCount || 0);
        const bCount = Number(a.ratingCount || 0);

        if (bRating !== aRating) return bRating - aRating;
        return bCount - aCount;
      });
    } else {
      products.sort((a, b) => {
        if (a.productURI === "d1-delay") return -1;
        if (b.productURI === "d1-delay") return 1;
        return 0;
      });
    }

    return products;
  }, [dataForAllProducts, searchTerm, sortBy, groupFilter]);

  const categoryCounts = useMemo(() => {
    return dataForAllProducts.reduce(
      (acc, product) => {
        const group = inferProductGroup(product);
        acc.all += 1;
        if (group === "plugins") acc.plugins += 1;
        if (group === "merch") acc.merch += 1;
        if (group === "other") acc.other += 1;
        return acc;
      },
      { all: 0, plugins: 0, merch: 0, other: 0 }
    );
  }, [dataForAllProducts]);

  return (
    <>
      <Seo
        title="Products | Parish Audio"
        description="Browse Parish Audio products. Professional tools and gear built for real-world music production."
        canonical="https://parishaudio.com/products"
        image="https://parishaudio.com/assets/HeroBanner1.png"
        imageAlt="Parish Audio products"
        ogType="website"
      />

      {filteredProducts.length > 0 && (
        <ProductListSchemaMarkup products={filteredProducts} />
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.25, md: 2 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1120px",
            margin: "0 auto",
            minHeight: height - 130,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Stack spacing={{ xs: 1.5, md: 2 }}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.25, sm: 1.5, md: 2 },
                  pt: { xs: 1.25, md: 1.5 },
                  pb: { xs: 1, md: 1.25 },
                  backgroundColor: style.darkPanel,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack spacing={{ xs: 1.1, md: 1.25 }}>
                  <Chip
                    icon={<StorefrontRoundedIcon />}
                    label="Store"
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      height: 28,
                    }}
                  />

                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "1.75rem", md: "2.3rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                      color: style.color4,
                    }}
                  >
                    All Products
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: style.color3,
                      maxWidth: "760px",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.65,
                    }}
                  >
                    Browse all plugins available from Parish Audio
                  </Typography>
                </Stack>
              </Box>

              <Box
                sx={{
                  px: { xs: 1, sm: 1.25, md: 1.5 },
                  py: { xs: 1, md: 1.25 },
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: style.darkPanel,
                    borderRadius: "14px",
                    p: { xs: 1, md: 1.1 },
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <Stack spacing={1.15}>
                    {/* <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`All (${categoryCounts.all})`}
                        onClick={() => setGroupFilter("all")}
                        sx={filterChipStyle(style, groupFilter === "all")}
                      />
                      <Chip
                        label={`Plugins (${categoryCounts.plugins})`}
                        onClick={() => setGroupFilter("plugins")}
                        sx={filterChipStyle(style, groupFilter === "plugins")}
                      />
                      <Chip
                        label={`Merch (${categoryCounts.merch})`}
                        onClick={() => setGroupFilter("merch")}
                        sx={filterChipStyle(style, groupFilter === "merch")}
                      />
                    </Stack> */}

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      sx={{ width: "100%", minWidth: 0 }}
                    >
                      <TextField
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search products..."
                        variant="outlined"
                        size="small"
                        sx={{
                          flex: 1.6,
                          minWidth: 0,
                          "& .MuiOutlinedInput-root": {
                            color: style.color4,
                            backgroundColor: "rgba(255,255,255,0.03)",
                            borderRadius: "8px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: style.color2,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: style.color3,
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: style.accentGreen,
                          },
                          "& .MuiInputBase-input::placeholder": {
                            color: style.color3,
                            opacity: 1,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: style.color4 }} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <FormControl
                        size="small"
                        sx={{
                          minWidth: { xs: "100%", md: 220 },
                          "& .MuiOutlinedInput-root": {
                            color: style.color4,
                            backgroundColor: "rgba(255,255,255,0.03)",
                            borderRadius: "8px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: style.color2,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: style.color3,
                          },
                          "& .MuiSvgIcon-root": {
                            color: style.color4,
                          },
                        }}
                      >
                        <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
                        <Select
                          value={sortBy}
                          label="Sort"
                          onChange={(e) => setSortBy(e.target.value)}
                          startAdornment={
                            !isMobile ? (
                              <InputAdornment position="start">
                                <TuneRoundedIcon sx={{ color: style.color4, ml: 1 }} />
                              </InputAdornment>
                            ) : undefined
                          }
                        >
                          <MenuItem value="featured">Featured</MenuItem>
                          <MenuItem value="rating">Highest Rated</MenuItem>
                          <MenuItem value="name-asc">Name: A to Z</MenuItem>
                          <MenuItem value="name-desc">Name: Z to A</MenuItem>
                          <MenuItem value="price-low">Price: Low to High</MenuItem>
                          <MenuItem value="price-high">Price: High to Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Stack>
                </Paper>

                <Typography
                  variant="body2"
                  sx={{
                    color: style.color3,
                    mt: 1,
                    mb: 0,
                    ml: 0.25,
                    fontSize: "0.9rem",
                  }}
                >
                  {loading
                    ? "Loading products..."
                    : `${filteredProducts.length} product${filteredProducts.length === 1 ? "" : "s"}`}
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                {loading ? (
                  <Box sx={{ py: 6 }}>
                    <LoadingSpinner />
                  </Box>
                ) : filteredProducts.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: style.color4,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
                      No products found
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3 }}>
                      Try a different search term or category.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {filteredProducts.map((product, index) => (
                      <Box
                        key={product.SKU || product.productURI || index}
                        sx={{
                          minWidth: 0,
                          width: "100%",
                          display: "flex",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            display: "flex",
                            marginBottom: 4,
                          }}
                        >
                          <HomePageProductCard productData={product} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}

function filterChipStyle(style, active) {
  return {
    borderRadius: "5px",
    fontWeight: 700,
    color: style.color4,
    backgroundColor: active ? style.accentGreen : "rgba(255,255,255,0.06)",
    border: active
      ? "1px solid rgba(255,255,255,0.10)"
      : "1px solid rgba(255,255,255,0.06)",
    "&:hover": {
      backgroundColor: active
        ? style.accentGreen
        : "rgba(255,255,255,0.10)",
    },
  };
}